import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Teachers.css";
import { FaUser } from "react-icons/fa";
import { PiBookOpenTextFill } from "react-icons/pi";
import { BsPlusCircleFill } from "react-icons/bs";
import { useDarkMode } from '../../contexts/DarkModeContext';
import { Link } from "react-router";
import { fetchTeachers, getChildSubjectTeachers, getAllTeachersForParent, getSubscribedTeachersForParent } from "../../redux/slices/teachersSlice";
import { getTeacherClasses, getTeacherStudentsByClass, getParentChildren, getChildSubjects } from "../../redux/slices/lessonsSlice";

function Teachers() {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const { teachers, loading, error, childSubjectTeachers, childSubjectTeachersLoading, allTeachersForParent, allTeachersForParentLoading, subscribedTeachersForParent, subscribedTeachersForParentLoading } = useSelector((state) => state.teachers);
  const { teacherClasses, teacherClassLessons, teacherStudents, lessonsLoading, studentsLoading, parentChildren, parentChildrenLoading, childSubjects, childSubjectsLoading } = useSelector((state) => state.lessons);
  const { currentUser } = useSelector((state) => state.auth);
  
  const [activeTab, setActiveTab] = useState(0);
  const [isGuestMode, setIsGuestMode] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(0);
  const [activeParentTab, setActiveParentTab] = useState("all"); // all, subjects, subscribed

  const userRole = currentUser?.role || localStorage.getItem("userRole");
  const isTeacher = userRole === "teacher";
  const isParent = userRole === "parent";

  // التحقق من وجود token عند تحميل المكون
  useEffect(() => {
    const token = localStorage.getItem("token3almny");
    if (token) {
      setIsGuestMode(false);
      if (isTeacher) {
        dispatch(getTeacherClasses());
      } else if (isParent) {
        dispatch(getParentChildren());
      } else {
        dispatch(fetchTeachers());
      }
    } else {
      setIsGuestMode(true);
      dispatch(fetchTeachers());
    }
  }, [dispatch, isTeacher, isParent]);

  // جلب الطلاب عند تغيير الفصل النشط (للمعلمين فقط)
  useEffect(() => {
    if (!isGuestMode && isTeacher && teacherClasses.length > 0) {
      const selectedClass = teacherClasses[activeTab];
      if (selectedClass?.id) {
        dispatch(getTeacherStudentsByClass({ page: 1, perPage: 6 }));
      }
    }
  }, [activeTab, teacherClasses, isGuestMode, isTeacher, dispatch]);

  // جلب مواد الابن عند تغيير الابن المختار (لولي الأمر)
  useEffect(() => {
    if (selectedChild) {
      console.log("جلب مواد الابن:", selectedChild.id);
      dispatch(getChildSubjects(selectedChild.id));
    }
  }, [selectedChild, dispatch]);

  // جلب مدرسين المادة عند تغيير الابن أو المادة (لولي الأمر)
  useEffect(() => {
    if (selectedChild) {
      console.log("جلب مدرسين المادة:", selectedChild.id, "المادة:", selectedSubjectId);
      dispatch(getChildSubjectTeachers({ 
        childId: selectedChild.id, 
        subjectId: selectedSubjectId,
        page: 1,
        perPage: 15
      }));
    }
  }, [selectedChild, selectedSubjectId, dispatch]);

  // تحديث selectedChild عند تغيير parentChildren
  useEffect(() => {
    if (parentChildren.length > 0 && !selectedChild) {
      const firstChild = parentChildren[0];
      setSelectedChild(firstChild);
      // جلب مواد ومدرسين الابن الأول مباشرة
      if (firstChild) {
        console.log("تعيين الابن الأول وجلب مواده ومدرسينه:", firstChild.id);
        dispatch(getChildSubjects(firstChild.id));
        dispatch(getChildSubjectTeachers({ 
          childId: firstChild.id, 
          subjectId: 0,
          page: 1,
          perPage: 15
        }));
      }
    }
  }, [parentChildren, selectedChild, dispatch]);

  // Mapping للأيقونات حسب اسم الفصل
  const getClassIcon = (className) => {
    const iconMap = {
      "الرياضيات": "/math.png",
      "الدراسات الاجتماعية": "/drasat.png",
      "اللغة العربية": "/arabic.png",
      "العلوم": "/science.png",
      "جغرافيا": "/gohraphia.png",
      "فيزياء": "/physic.png",
      "اللغة الإنجليزية": "/english.png",
      "تاريخ": "/tarekh.png",
      "اللغة الانجليزية": "/english.png",
    };
    return iconMap[className] || "/science.png";
  };

  const handleTabClick = (index) => {
    setActiveTab(index);
  };

  // دالة تغيير المادة لولي الأمر
  const handleSubjectChange = (subjectId) => {
    console.log("تغيير المادة إلى:", subjectId);
    setSelectedSubjectId(subjectId);
  };

  if (loading || lessonsLoading || studentsLoading) {
    return (
      <section className={`teachers-section ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // للطلاب: عرض المدرسين العاديين
  if (!isTeacher && !isParent) {
    // Ensure teachers is an array and has data, limit to 6 teachers
    const teachersArray = Array.isArray(teachers) ? teachers.slice(0, 6) : [];

    return (
      <section className={`teachers-section ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        {/* Floating Animation Elements */}
        <div className="floating-elements">
          <div className="floating-element element-1">👨‍🏫</div>
          <div className="floating-element element-2">📚</div>
          <div className="floating-element element-3">🔬</div>
          <div className="floating-element element-4">🧮</div>
          <div className="floating-element element-5">⭐</div>
          <div className="floating-element element-6">🎓</div>
        </div>

        <div className="container">
          <div className="top-header">
            <div className="right-texts">
              <p className="category-title">مدرسين علمني</p>
              <h2 className="main-title mb-2">مدرسين ذو خبرة عالية</h2>
            </div>
            <Link to="/teachers" className="view-more text-decoration-none">عرض المزيد</Link>
          </div>

          <div className="teachers-grid">
            {teachersArray.length > 0 ? (
              teachersArray.map((teacher, index) => (
                <Link to={`/teacherDetails/${teacher.id || index}`} className="text-decoration-none" key={teacher.id || index}>
                  <div className="teacher-card">
                    <div className="teacher-image">
                      <img 
                        src={teacher.prof_img || teacher.image || "https://randomuser.me/api/portraits/men/32.jpg"} 
                        alt={teacher.name} 
                        onError={(e) => {
                          e.target.src = "https://randomuser.me/api/portraits/men/32.jpg";
                        }}
                      />
                      <BsPlusCircleFill className="plus-icon" />
                    </div>
                    <h5 className="teacher-name mb-3" style={{fontSize : "15px"}}>{teacher.name}</h5>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center w-100">
                <p>لا توجد بيانات للمدرسين</p>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // لولي الأمر: عرض مدرسين المواد للأبناء
  if (isParent) {
    return (
      <section className={`teachers-section ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
        {/* Floating Animation Elements */}
        <div className="floating-elements">
          <div className="floating-element element-1">👨‍🏫</div>
          <div className="floating-element element-2">📚</div>
          <div className="floating-element element-3">🔬</div>
          <div className="floating-element element-4">🧮</div>
          <div className="floating-element element-5">⭐</div>
          <div className="floating-element element-6">🎓</div>
        </div>

        <div className="container">
          <div className="top-header">
            <div className="right-texts">
              <p className="category-title">مدرسين الأبناء</p>
              <h2 className="main-title mb-2">مدرسين المواد الدراسية</h2>
            </div>
          </div>

          {/* Dropdown لاختيار الابن */}
          <div className="child-selector mb-4">
            <label htmlFor="childSelect" className="form-label fw-bold text-primary mb-2">
              اختر الابن:
            </label>
            <select
              id="childSelect"
              className="form-select"
              value={selectedChild?.id || ""}
              onChange={(e) => {
                console.log("تم تغيير الابن إلى:", e.target.value);
                const child = parentChildren.find(c => c.id == e.target.value);
                console.log("الابن المختار:", child);
                setSelectedChild(child);
                // إعادة تعيين المادة إلى الكل عند تغيير الابن
                setSelectedSubjectId(0);
              }}
              disabled={parentChildrenLoading}
            >
              {parentChildrenLoading ? (
                <option>جاري التحميل...</option>
              ) : parentChildren.length > 0 ? (
                parentChildren.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))
              ) : (
                <option>لا يوجد أبناء</option>
              )}
            </select>
          </div>

          {/* Tabs المواد */}
          {selectedChild && (
            <div className="subjects-tabs mt-3">
              <div className="tabs-container">
                {/* خيار الكل */}
                <button
                  className={`subject-tab ${selectedSubjectId === 0 ? 'active' : ''}`}
                  onClick={() => handleSubjectChange(0)}
                >
                  <img 
                    src="/science.png" 
                    alt="الكل" 
                    className="tab-icon" 
                  />
                  <span>الكل</span>
                </button>
                
                {/* باقي المواد */}
                {childSubjects?.data && childSubjects.data.length > 0 && childSubjects.data.map((subject) => (
                  <button
                    key={subject.id}
                    className={`subject-tab ${selectedSubjectId === subject.id ? 'active' : ''}`}
                    onClick={() => handleSubjectChange(subject.id)}
                  >
                    <img 
                      src="/science.png" 
                      alt={subject.title} 
                      className="tab-icon" 
                    />
                    <span>{subject.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* عرض المدرسين */}
          <div className="teachers-grid">
            {childSubjectTeachersLoading ? (
              <div className="text-center w-100">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
              </div>
            ) : childSubjectTeachers?.data && childSubjectTeachers.data.length > 0 ? (
              childSubjectTeachers.data.map((teacher, index) => (
                <Link to={`/teacherDetails/${teacher.id}`} className="text-decoration-none" key={teacher.id || index}>
                  <div className="teacher-card">
                    <div className="teacher-image">
                      <img 
                        src={teacher.prof_img || "https://randomuser.me/api/portraits/men/32.jpg"} 
                        alt={teacher.name} 
                        onError={(e) => {
                          e.target.src = "https://randomuser.me/api/portraits/men/32.jpg";
                        }}
                      />
                      <BsPlusCircleFill className="plus-icon" />
                    </div>
                    <h5 className="teacher-name mb-3" style={{fontSize : "15px"}}>{teacher.name}</h5>
                    <div className="teacher-info">
                      <span className="teacher-subject">{teacher.subject_name}</span>
                      <div className="teacher-rating">
                        <span className="rating-value">{teacher.average_rate}</span>
                        <span className="rating-label">/ 5</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="text-center w-100">
                <p>لا توجد بيانات للمدرسين في هذه المادة</p>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // للمعلمين: عرض الفصول مع الطلاب
  return (
    <section className={`teachers-section ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Floating Animation Elements */}
      <div className="floating-elements">
        <div className="floating-element element-1">👨‍🏫</div>
        <div className="floating-element element-2">📚</div>
        <div className="floating-element element-3">🔬</div>
        <div className="floating-element element-4">🧮</div>
        <div className="floating-element element-5">⭐</div>
        <div className="floating-element element-6">🎓</div>
      </div>

      <div className="container">
        <div className="top-header">
          <div className="right-texts">
            <p className="category-title">طلابي</p>
            <h2 className="main-title mb-2">طلاب الفصول الدراسية</h2>
          </div>
        </div>

        {/* Class Tabs for Teachers */}
        {!isGuestMode && teacherClasses.length > 0 && (
          <div className="subjects-tabs mt-3">
            <div className="tabs-container">
              {teacherClasses.map((classItem, index) => (
                <button
                  key={classItem.id}
                  className={`subject-tab ${activeTab === index ? 'active' : ''}`}
                  onClick={() => handleTabClick(index)}
                >
                  <img 
                    src={getClassIcon(classItem.name)} 
                    alt={classItem.name} 
                    className="tab-icon" 
                  />
                  <span>{classItem.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Students Grid */}
        <div className="teachers-grid">
          {teacherStudents.length > 0 ? (
            teacherStudents.map((student, index) => (
              <div key={student.id || index} className="teacher-card">
                <div className="teacher-image">
                  <img 
                    src={student.prof_img || student.image || "https://randomuser.me/api/portraits/men/32.jpg"} 
                    alt={student.name} 
                    onError={(e) => {
                      e.target.src = "https://randomuser.me/api/portraits/men/32.jpg";
                    }}
                  />
                  <BsPlusCircleFill className="plus-icon" />
                </div>
                <h5 className="teacher-name mb-3" style={{fontSize : "15px"}}>{student.name}</h5>
                <div className="student-info">
                  <span className="student-class">{student.class_name}</span>
                  <span className="student-subject">{student.subject_name}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center w-100">
              <p>لا توجد بيانات للطلاب في هذا الفصل</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Teachers;
